import { ChangeDetectionStrategy, Component, input, model, output, ViewEncapsulation } from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { ValidationError } from '@angular/forms/signals';
import { CheckboxComponent, CheckboxSize } from './checkbox.component';

export interface CheckboxGroupOption<T> {
  readonly value: T;
  readonly label: string;
  readonly description?: string;
  readonly disabled?: boolean;
}

@Component({
  selector: 'wb-checkbox-group',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CheckboxComponent],
  templateUrl: './checkbox.group.component.html',
  styleUrls: ['./checkbox.group.component.less'],
  host: {
    role: 'group',
    '[class.is-horizontal]': '$orientation() === "horizontal"',
    '[attr.aria-disabled]': 'disabled()',
    '[attr.hidden]': 'hidden() ? "" : null',
  },
})
export class CheckboxGroupComponent<T = unknown> implements FormValueControl<T[]> {

  public readonly value = model<T[]>([]);
  public readonly disabled = input(false);
  public readonly readonly = input(false);
  public readonly touched = input(false);
  public readonly touch = output<void>();
  public readonly errors = input<readonly ValidationError.WithOptionalFieldTree[]>([]);
  public readonly invalid = input(false);
  public readonly required = input(false);
  public readonly hidden = input(false);
  public readonly pending = input(false);
  public readonly dirty = input(false);
  public readonly name = input('');

  public readonly $options = input<readonly CheckboxGroupOption<T>[]>([], { alias: 'options' });
  public readonly $orientation = input<'vertical' | 'horizontal'>('vertical', { alias: 'orientation' });
  public readonly $size = input<CheckboxSize>('small', { alias: 'size' });
  public readonly $compareWith = input<(left: T, right: T) => boolean>(Object.is, { alias: 'compareWith' });

  public toggle(option: CheckboxGroupOption<T>, checked: boolean): void {

    if (this.disabled() || this.readonly() || option.disabled) return;
    const selected = this.value();
    const exists = selected.some(value => this.$compareWith()(value, option.value));
    if (checked && !exists) this.value.set([...selected, option.value]);
    if (!checked && exists) this.value.set(selected.filter(value => !this.$compareWith()(value, option.value)));
    this.touch.emit();
  }

  protected isChecked(value: T): boolean {

    return this.value().some(selected => this.$compareWith()(selected, value));
  }
}
